// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: CHECKLISTS
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Migration i18n (mai 2026). Note : la variable locale "template" était
// nommée `t` dans la version antérieure — renommée en `tpl` pour ne pas
// shadow la fonction globale `t(...)` du système i18n.

import { definePage } from '../router.js';

interface GenericRoleRow {
  code: string;
  name: string;
}

interface TemplateAction {
  id?: number;
  label: string;
  order: number;
  // R = Réalise (multiple). A = Approuve (single). Renommés en mai 2026 :
  // ancien `responsible_role` (R, single) → `accountable_role` (A, single) ;
  // ancien `accountable_roles` (A, multi) → `responsible_roles` (R, multi).
  accountable_role?: string | null;
  responsible_roles?: string[];
  consulted_roles?: string[];
  informed_roles?: string[];
}

interface Template {
  id: number;
  name: string;
  created_at?: string | null;
  actions: TemplateAction[];
}

declare global {
  interface Window {
    renderChecklistsPage: typeof renderChecklistsPage;
    renderChecklistDetail: typeof renderChecklistDetail;
    showTemplateActionModal: typeof showTemplateActionModal;
  }
}

// `checklists.read` garde les deux routes de ce fichier, mais c'est une clé de
// menu : elle n'autorise pas l'écriture (revue front §I3).
const canWriteChecklists = (): boolean =>
  !!(window.hasPermission && window.hasPermission('checklists.write'));

pages.adminChecklists = async function () {
  setHeader(
    t('checklists.page_title'),
    canWriteChecklists()
      ? `<button class="btn btn-secondary ml-auto" id="btn-ei-checklists" style="margin-right:6px">${esc(t('common.export_import'))}</button>` +
          `<button class="btn btn-primary" id="btn-new-tpl">${esc(t('checklists.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const templates = (await api.getTemplates()) as Template[];
    renderChecklistsPage(templates);
    document.getElementById('btn-ei-checklists')?.addEventListener('click', () => {
      const examples = window.EXPORT_EXAMPLES['checklists'];
      window.showExportImportModal(
        'checklists',
        t('checklists.page_title'),
        examples?.json ?? '',
        examples?.csv ?? '',
      );
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderChecklistsPage(templates: Template[]): void {
  let sortKey: 'name' | 'actions' | 'created' = 'name';
  let sortDir: 'asc' | 'desc' = 'asc';

  const sort = (arr: ReadonlyArray<Template>): Template[] =>
    [...arr].sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortKey) {
        case 'name':
          va = a.name.toLowerCase();
          vb = b.name.toLowerCase();
          break;
        case 'actions':
          va = a.actions.length;
          vb = b.actions.length;
          break;
        case 'created':
          va = a.created_at || '';
          vb = b.created_at || '';
          break;
        default:
          va = a.name.toLowerCase();
          vb = b.name.toLowerCase();
      }
      return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
    });

  const th = (key: 'name' | 'actions' | 'created', label: string): string => {
    const active = sortKey === key;
    return `<th data-sort-tpl="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span style="font-size:10px">${active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span></th>`;
  };

  const buildRows = (): string =>
    sort(templates)
      .map(
        (tpl) => `<tr>
    <td style="cursor:${canWriteChecklists() ? 'pointer' : 'default'};font-size:13px;font-weight:500" ${canWriteChecklists() ? `data-rename-tpl="${tpl.id}" title="${esc(t('checklists.cell.rename_title'))}"` : ''}>${esc(tpl.name)}</td>
    <td style="font-size:12px;color:var(--text3)">${esc(t('checklists.action_count', { n: tpl.actions.length }))}</td>
    <td style="font-size:12px;color:var(--text3)">${tpl.created_at ? esc(fmtDateLocal(tpl.created_at)) : '—'}</td>
    <td style="white-space:nowrap">
      <a href="#admin/checklists/${tpl.id}" class="btn btn-secondary btn-sm">${esc(t('checklists.btn.manage'))}</a>${
        canWriteChecklists()
          ? `
      <button class="btn btn-danger btn-icon" data-del-tpl="${tpl.id}" data-tpl-name="${esc(tpl.name)}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }
    </td>
  </tr>`,
      )
      .join('');

  const emptyRow = `<tr><td colspan="4" class="empty">${esc(t('checklists.empty'))}</td></tr>`;

  setContent(`<div class="card" id="tpl-card"><div class="table-wrap"><table>
    <thead><tr>${th('name', t('checklists.col.name'))}${th('actions', t('vocabulary.action_plural'))}${th('created', t('checklists.col.created_at'))}<th></th></tr></thead>
    <tbody id="tpl-tbody">${buildRows() || emptyRow}</tbody>
  </table></div></div>`);

  const reRender = (): void => {
    const tbody = document.getElementById('tpl-tbody');
    if (tbody) tbody.innerHTML = buildRows() || emptyRow;
    document.querySelectorAll<HTMLElement>('[data-sort-tpl]').forEach((h) => {
      const a = h.dataset['sortTpl'] === sortKey;
      const headerText = (h.textContent || '').replace(/[↑↓↕]/g, '').trim();
      h.innerHTML =
        esc(headerText) +
        ' <span style="font-size:10px">' +
        (a ? (sortDir === 'asc' ? '↑' : '↓') : '↕') +
        '</span>';
    });
  };

  document.getElementById('tpl-card')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const sortTh = target.closest<HTMLElement>('[data-sort-tpl]');
    if (sortTh) {
      const key = sortTh.dataset['sortTpl'] as 'name' | 'actions' | 'created';
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = 'asc';
      }
      reRender();
      return;
    }
    const renameCell = target.closest<HTMLElement>('[data-rename-tpl]');
    if (renameCell && !target.closest('button') && !target.closest('a')) {
      const id = parseInt(renameCell.dataset['renameTpl'] || '0');
      const tpl = templates.find((x) => x.id === id);
      if (!tpl) return;
      openModal(
        t('checklists.modal.rename.title'),
        `<div class="form-group"><label>${esc(t('common.field.name'))}</label>
          <input type="text" id="rename-tpl-input" value="${esc(tpl.name)}" placeholder="${esc(t('checklists.field.name.placeholder'))}" style="width:100%"></div>`,
        async () => {
          const input = document.getElementById('rename-tpl-input') as HTMLInputElement | null;
          const name = input?.value.trim() || '';
          if (!name) {
            toast(t('common.error.name_required'), 'error');
            return;
          }
          try {
            await api.renameTemplate(tpl.id, name);
            closeModal();
            toast(t('checklists.toast.renamed'));
            tpl.name = name;
            reRender();
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.save'),
      );
      setTimeout(() => {
        const inp = document.getElementById('rename-tpl-input') as HTMLInputElement | null;
        inp?.select();
      }, 50);
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-del-tpl]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['delTpl'] || '0');
      const name = delBtn.dataset['tplName'] || '';
      openModal(
        t('checklists.modal.delete.title'),
        `<p>${t('checklists.modal.delete.message', { name })}</p>`,
        async () => {
          try {
            await api.deleteTemplate(id);
          } catch (e) {
            // 409 si la checklist est encore référencée (pas de delete cascade).
            toast(e instanceof Error ? e.message : t('common.error'), 'error');
            return;
          }
          closeModal();
          toast(t('checklists.toast.deleted'));
          templates.splice(
            templates.findIndex((x) => x.id === id),
            1,
          );
          renderChecklistsPage(templates);
        },
        t('common.delete'),
      );
    }
  });

  document.getElementById('btn-new-tpl')?.addEventListener('click', () => {
    openModal(
      t('checklists.modal.create.title'),
      `<div class="form-group"><label>${esc(t('checklists.field.name.label'))}</label>
        <input type="text" id="nt-name" value="" style="width:100%" placeholder="${esc(t('checklists.field.name.placeholder'))}"></div>`,
      async () => {
        const input = document.getElementById('nt-name') as HTMLInputElement | null;
        const name = input?.value.trim() || '';
        if (!name) {
          toast(t('common.error.name_required'), 'error');
          return;
        }
        let tpl: Template;
        try {
          tpl = (await api.createTemplate({ name, actions: [] })) as Template;
        } catch (e) {
          toast(e instanceof Error ? e.message : t('common.error'), 'error');
          return;
        }
        closeModal();
        toast(t('checklists.toast.created'));
        router.navigate('#admin/checklists/' + tpl.id);
      },
      t('common.add_short'),
    );
  });
}

pages.adminChecklistDetail = definePage<'admin/checklists/:id'>(async ({ id }) => {
  const tid = id;
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [tpl, roles] = await Promise.all([
      api.getTemplate(tid) as Promise<Template>,
      api.getGenericRoles() as Promise<GenericRoleRow[]>,
    ]);
    renderChecklistDetail(tpl, roles);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

function renderChecklistDetail(tpl: Template, roles: ReadonlyArray<GenericRoleRow>): void {
  setHeader(
    `<a href="#admin/checklists" style="color:var(--accent);text-decoration:underline">${esc(t('checklists.page_title'))}</a> / ${esc(tpl.name)}`,
    canWriteChecklists()
      ? `<button class="btn btn-primary ml-auto" id="btn-add-action">${esc(t('checklist_detail.btn.add_action'))}</button>`
      : '',
  );

  const roleMap: Record<string, GenericRoleRow> = Object.fromEntries(roles.map((r) => [r.code, r]));
  const rolePill = (code: string | null | undefined): string => {
    if (!code) return '<span style="color:var(--text3);font-size:11px">—</span>';
    const r = roleMap[code];
    return `<span style="font-size:11px;background:var(--accent-bg);color:var(--accent);padding:1px 7px;border-radius:8px">${esc(r ? r.name : code)}</span>`;
  };

  let actions: TemplateAction[] = [...tpl.actions].sort((a, b) => a.order - b.order);

  const renderTable = (): string => {
    const rows = actions
      .map(
        (a) => `<tr>
      <td style="color:var(--text3);text-align:center;font-size:12px;width:32px">${a.order + 1}</td>
      <td style="font-size:13px;cursor:${canWriteChecklists() ? 'pointer' : 'default'}" ${canWriteChecklists() ? `data-click-ta="${a.id}"` : ''}>${esc(a.label)}</td>
      <td>${(a.responsible_roles || []).map((r) => rolePill(r)).join(' ') || rolePill(null)}</td>
      <td>${rolePill(a.accountable_role)}</td>
      <td style="font-size:11px;color:var(--text3)">${[...(a.consulted_roles || []), ...(a.informed_roles || [])].map((c) => rolePill(c)).join(' ') || '—'}</td>
      <td style="white-space:nowrap">${
        canWriteChecklists()
          ? `<button class="btn btn-secondary btn-icon" data-edit-ta="${a.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon" data-del-ta="${a.id}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }</td>
    </tr>`,
      )
      .join('');

    return `<div class="card" id="ta-card"><div class="table-wrap"><table>
      <thead><tr><th style="width:32px">#</th><th>${esc(t('checklist_detail.col.label'))}</th><th>${esc(t('checklist_detail.col.r'))}</th><th>${esc(t('checklist_detail.col.a'))}</th><th>${esc(t('checklist_detail.col.ci'))}</th><th></th></tr></thead>
      <tbody>${rows || `<tr><td colspan="6" class="empty">${esc(t('checklist_detail.empty'))}</td></tr>`}</tbody>
    </table></div></div>`;
  };

  setContent(renderTable());

  const reRender = (): void => {
    const card = document.getElementById('ta-card');
    if (card) card.outerHTML = renderTable();
    wireTACard();
  };
  wireTACard();

  function wireTACard(): void {
    const card = document.getElementById('ta-card');
    if (!card) return;
    card.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const clickCell = target.closest<HTMLElement>('[data-click-ta]');
      const editBtn = target.closest<HTMLElement>('[data-edit-ta]');
      const delBtn = target.closest<HTMLElement>('[data-del-ta]');

      if ((clickCell || editBtn) && !target.closest('button')) {
        const id = parseInt(clickCell?.dataset['clickTa'] || editBtn?.dataset['editTa'] || '0');
        const ta = actions.find((x) => x.id === id);
        if (ta)
          showTemplateActionModal(ta, roles, tpl.id, (updated) => {
            const idx = actions.findIndex((x) => x.id === ta.id);
            if (idx !== -1) actions[idx] = updated;
            reRender();
          });
        return;
      }
      if (editBtn) {
        const id = parseInt(editBtn.dataset['editTa'] || '0');
        const ta = actions.find((x) => x.id === id);
        if (ta)
          showTemplateActionModal(ta, roles, tpl.id, (updated) => {
            const idx = actions.findIndex((x) => x.id === ta.id);
            if (idx !== -1) actions[idx] = updated;
            reRender();
          });
        return;
      }
      if (delBtn) {
        const taId = parseInt(delBtn.dataset['delTa'] || '0');
        openModal(
          t('checklist_detail.modal.delete.title'),
          `<p>${esc(t('checklist_detail.modal.delete.message'))}</p>`,
          async () => {
            // On ne mute le tableau local qu'APRÈS confirmation serveur :
            // en cas d'échec, l'état affiché reste cohérent avec la base.
            const next = actions.filter((x) => x.id !== taId);
            try {
              await api.updateTemplate(tpl.id, { name: tpl.name, actions: next });
            } catch (e) {
              toast(e instanceof Error ? e.message : t('common.error'), 'error');
              return;
            }
            actions = next;
            closeModal();
            toast(t('checklists.toast.deleted'));
            reRender();
          },
          t('common.delete'),
        );
      }
    });
    document.getElementById('btn-add-action')?.addEventListener('click', () => {
      showTemplateActionModal(null, roles, tpl.id, (created) => {
        actions.push(created);
        reRender();
      });
    });
  }
}

function showTemplateActionModal(
  ta: TemplateAction | null,
  roles: ReadonlyArray<GenericRoleRow>,
  templateId: number,
  onSaved: (saved: TemplateAction) => void,
): void {
  const isNew = !ta;
  // L'approbateur (A) est rendu directement avec son option `selected` plutôt
  // que posé via setTimeout après l'ouverture : sinon le snapshot anti-perte
  // de modifications (openWideModal) capture la valeur AVANT le setTimeout et
  // croit à tort que le champ a été modifié → prompt parasite à la fermeture.
  const acctSel = ta?.accountable_role || '';
  const roleOpts =
    `<option value=""${acctSel === '' ? ' selected' : ''}>—</option>` +
    roles
      .map(
        (r) =>
          `<option value="${r.code}"${r.code === acctSel ? ' selected' : ''}>${esc(r.code)} — ${esc(r.name)}</option>`,
      )
      .join('');
  const multiRoleCheckboxes = (field: string, selectedCodes: ReadonlyArray<string> = []): string =>
    roles
      .map(
        (r) => `
    <label style="display:flex;align-items:center;gap:5px;margin-bottom:3px;font-size:12px;cursor:pointer">
      <input type="checkbox" class="ta-${field}" value="${esc(r.code)}" ${selectedCodes.includes(r.code) ? 'checked' : ''}>
      ${esc(r.code)} — ${esc(r.name)}
    </label>`,
      )
      .join('');

  const body = `
    <div class="form-group"><label>${esc(t('checklist_detail.action_modal.label'))}</label>
      <input type="text" id="ta-label" value="${esc(ta?.label || '')}" style="width:100%" placeholder="${esc(t('checklist_detail.action_modal.placeholder'))}"></div>
    <div class="form-row" style="margin-top:10px">
      <div class="form-group" style="flex:1"><label>${esc(t('checklist_detail.action_modal.r'))}</label>
        <div style="max-height:260px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius);padding:6px 8px">
          ${multiRoleCheckboxes('responsible', ta?.responsible_roles)}
        </div></div>
      <div class="form-group" style="flex:1"><label>${esc(t('checklist_detail.action_modal.a'))}</label>
        <select id="ta-acct" style="width:100%">${roleOpts}</select></div>
    </div>
    <div class="form-row" style="margin-top:10px">
      <div class="form-group" style="flex:1"><label>${esc(t('checklist_detail.action_modal.c'))}</label>
        <div style="max-height:260px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius);padding:6px 8px">
          ${multiRoleCheckboxes('consulted', ta?.consulted_roles)}
        </div></div>
      <div class="form-group" style="flex:1"><label>${esc(t('checklist_detail.action_modal.i'))}</label>
        <div style="max-height:260px;overflow-y:auto;border:1px solid var(--border);border-radius:var(--radius);padding:6px 8px">
          ${multiRoleCheckboxes('informed', ta?.informed_roles)}
        </div></div>
    </div>`;

  openWideModal(
    isNew
      ? t('checklist_detail.action_modal.create.title')
      : t('checklist_detail.action_modal.update.title'),
    body,
    async () => {
      const labelEl = document.getElementById('ta-label') as HTMLInputElement | null;
      const label = labelEl?.value.trim() || '';
      if (!label) {
        toast(t('checklist_detail.error.label_required'), 'error');
        return;
      }
      const acctEl = document.getElementById('ta-acct') as HTMLSelectElement | null;
      const acct = acctEl?.value || null;
      const resp = Array.from(
        document.querySelectorAll<HTMLInputElement>('.ta-responsible:checked'),
      ).map((c) => c.value);
      const consulted = Array.from(
        document.querySelectorAll<HTMLInputElement>('.ta-consulted:checked'),
      ).map((c) => c.value);
      const informed = Array.from(
        document.querySelectorAll<HTMLInputElement>('.ta-informed:checked'),
      ).map((c) => c.value);
      const order = isNew ? 999 : (ta?.order ?? 0);
      const current = (await api.getTemplate(templateId)) as Template;
      const updatedActions: TemplateAction[] = current.actions.map((a) => ({
        label: a.label,
        order: a.order,
        accountable_role: a.accountable_role ?? null,
        responsible_roles: a.responsible_roles ?? [],
        consulted_roles: a.consulted_roles ?? [],
        informed_roles: a.informed_roles ?? [],
      }));
      const newAction: TemplateAction = {
        label,
        order,
        accountable_role: acct,
        responsible_roles: resp,
        consulted_roles: consulted,
        informed_roles: informed,
      };
      if (isNew) {
        newAction.order = updatedActions.length;
        updatedActions.push(newAction);
      } else if (ta) {
        // Le PUT backend remplace toutes les actions (delete + recreate) →
        // les `id` sont réattribués à chaque save. On ne peut donc PAS matcher
        // l'action éditée par son `id` (périmé dès la 2e édition d'une même
        // session). On matche par `order`, stable et préservé par le backend.
        const idx = updatedActions.findIndex((_, i) => current.actions[i]?.order === ta.order);
        if (idx !== -1) updatedActions[idx] = newAction;
      }
      const saved = (await api.updateTemplate(templateId, {
        name: current.name,
        actions: updatedActions,
      })) as Template;
      closeModal();
      toast(isNew ? t('checklist_detail.toast.created') : t('checklist_detail.toast.updated'));
      const savedAction = isNew
        ? saved.actions[saved.actions.length - 1]
        : saved.actions.find((a) => a.label === label && a.order === order);
      onSaved(savedAction || newAction);
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderChecklistsPage = renderChecklistsPage;
window.renderChecklistDetail = renderChecklistDetail;
window.showTemplateActionModal = showTemplateActionModal;

export {};
